import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { CommandContribution, CommandRegistry } from '@patsnap/tahiti-core';
import { Message } from '@patsnap/tahiti-core';
import { imageSelectorDialogServiceFactory } from '../services/impls/image-selector-dialog-service';
import type { ImageSelectorDialogResult } from '../services/impls/image-selector-dialog-service';
import { ImageProviderService } from '../services/image-provider-service';
import { ImageSaveService } from '../services/image-save-service';
import { FigureAddService } from '@/domains/specification/services/figure-add-service';
import { I18nService } from '@telecom/shared/services/i18n';

/**
 * 图片选择命令选项
 */
interface ImageSelectorOptions {
    /** 对话框标题 */
    title?: string;
    /** 最大选择数量 */
    maxSelection?: number;
    /** 已选择的图片ID */
    selectedImages?: string[];
}

/**
 * 图片选择命令贡献类
 * 提供打开图片选择对话框的命令
 */
@injectable()
export class ImageSelectorCommandContribution implements CommandContribution {
    private readonly _dialogService = imageSelectorDialogServiceFactory();

    // eslint-disable-next-line max-params
    constructor(
        @inject(Message)
        private readonly _message: Message,
        @inject(ImageProviderService)
        private readonly _imageProvider: ImageProviderService,
        @inject(ImageSaveService)
        private readonly _imageSaveService: ImageSaveService,
        @inject(FigureAddService)
        private readonly _figureAddService: FigureAddService,
        @inject(I18nService)
        private readonly _i18nService: I18nService,
    ) {}

    registerCommands(commands: CommandRegistry): void {
        commands.registerCommand({
            id: 'spec-claims:select-images',
        }, {
            execute: async (options: ImageSelectorOptions = {}): Promise<ImageSelectorDialogResult | null> => {
                return this._executeImageSelection(options);
            },
        });
    }

    /**
     * 执行图片选择流程
     */
    private async _executeImageSelection(options: ImageSelectorOptions): Promise<ImageSelectorDialogResult | null> {
        const availableImages = await this._fetchAvailableImages();
        const dialogOptions = this._buildDialogOptions(options, availableImages);

        this._setupSubmitHandler();

        return await this._dialogService.open(dialogOptions);
    }

    /**
     * 获取可用图片列表
     */
    private async _fetchAvailableImages() {
        return this._imageProvider.getAvailableImages({
            maxCount: 100,
            imageTypes: ['jpg', 'jpeg', 'png', 'gif', 'webp'],
        });
    }

    /**
     * 构建对话框选项
     */
    private _buildDialogOptions(options: ImageSelectorOptions, availableImages: any[]) {
        return {
            title: options.title || this._i18nService.t('specClaimsImages.uploadArea.selectImagesFromFiles'),
            maxSelection: options.maxSelection || 15,
            selectedImages: options.selectedImages || [],
            availableImages,
        };
    }

    /**
     * 设置对话框提交处理器
     */
    private _setupSubmitHandler(): void {
        this._dialogService.onSubmit(async (data, next) => {
            try {
                await this._handleImageSaving(data);
                next();
            } catch (error) {
                this._handleSaveError();
                next(false);
            }
        });
    }

    /**
     * 处理图片保存
     */
    private async _handleImageSaving(data: any): Promise<void> {
        if (!data.selectedImages?.length) {
            return;
        }

        const savedImages = await this._imageSaveService.saveSelectedImages({
            images: data.selectedImages,
        });

        if (savedImages.length > 0) {
            this._figureAddService.addFigures(savedImages);
            this._showSaveSuccess(savedImages.length);
        } else {
            this._showSaveFailure();
        }
    }

    /**
     * 显示保存成功消息
     */
    private _showSaveSuccess(count: number): void {
        this._message.success({
            message: this._i18nService.t('specClaimsImages.imageSelector.saveSuccess', { count }),
        });
    }

    /**
     * 显示保存失败消息
     */
    private _showSaveFailure(): void {
        this._message.warning({
            message: this._i18nService.t('specClaimsImages.imageSelector.saveFailure'),
        });
    }

    /**
     * 处理保存错误
     */
    private _handleSaveError(): void {
        this._message.error({
            message: this._i18nService.t('specClaimsImages.imageSelector.saveFailure'),
        });
    }

    /**
     * 便捷方法：打开图片选择对话框
     * @param options 对话框选项
     * @returns 选择结果或null（用户取消）
     */
    async openImageSelector(options: ImageSelectorOptions = {}): Promise<ImageSelectorDialogResult | null> {
        return this._dialogService.open(options);
    }
}

// 扩展CommandService类型定义
declare module '@patsnap/tahiti-core' {
    export interface CommandService {
        /**
         * 打开图片选择对话框
         * @param commandId - 命令ID: 'spec-claims:select-images'
         * @param options - 对话框选项
         * @returns 选择的图片结果，用户取消时返回null
         */
        executeCommand(commandId: 'spec-claims:select-images', options?: ImageSelectorOptions): Promise<ImageSelectorDialogResult | null>;
    }
}
